import { useEffect, useState } from "react";
import api from "../../services/api";
import { Search, ShoppingBag, Trash2, Edit2, X, Eye } from "lucide-react";
import toast from "react-hot-toast";

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <td key={i} className="px-6 py-4">
          <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
        </td>
      ))}
    </tr>
  );
}

export default function InstituteOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewModal, setViewModal] = useState({ open: false, order: null });
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const load = () => {
    setLoading(true);
    api.get("/institute/orders")
      .then(res => setOrders(res.data))
      .catch(() => toast.error("Failed to load orders"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  

  const filtered = orders.filter(o =>
    o.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    o.course_title?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const totalRevenue = filtered.reduce((sum, o) => sum + (o.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Orders</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {orders.length} total orders · Total Revenue:{" "}
            <span className="text-emerald-600 font-bold">₹{totalRevenue.toLocaleString()}</span>
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by user or course..."
          className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-6 py-3.5 text-left font-semibold">Order ID</th>
                <th className="px-6 py-3.5 text-left font-semibold">User</th>
                <th className="px-6 py-3.5 text-left font-semibold">Course</th>
                <th className="px-6 py-3.5 text-left font-semibold">Amount</th>
                <th className="px-6 py-3.5 text-left font-semibold">Payment Mode</th>
                <th className="px-6 py-3.5 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading
                ? [1, 2, 3, 4, 5].map(i => <SkeletonRow key={i} />)
                : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-16 text-center">
                      <ShoppingBag size={40} className="mx-auto text-slate-300 dark:text-slate-700 mb-3" />
                      <p className="text-slate-500 font-medium">No orders found</p>
                    </td>
                  </tr>
                ) : paginated.map(order => (
                  <tr key={order.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 text-slate-500 dark:text-slate-400 font-mono text-xs">
                      #{order.id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                          {order.user_email?.[0]?.toUpperCase()}
                        </div>
                        <span className="text-slate-700 dark:text-slate-300 font-medium">{order.user_email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-slate-800 dark:text-white line-clamp-1">{order.course_title}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-slate-900 dark:text-white">
                        {order.amount > 0 ? `₹${order.amount}` : (
                          <span className="text-emerald-500 font-semibold">Free</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ${
                        order.payment_mode === "Cash" 
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                          : order.payment_mode === "Free"
                          ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                          : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                      }`}>
                        {order.payment_mode || "Online"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewModal({ open: true, order: order })}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(order)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete Order"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t dark:border-slate-800 flex items-center justify-between">
            <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold dark:text-white">Order Details</h3>
              <button onClick={() => setViewModal({ open: false, order: null })} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b dark:border-slate-800">
                <span className="text-sm text-slate-500">Order ID</span>
                <span className="text-sm font-mono font-bold dark:text-white">#{viewModal.order.id}</span>
              </div>
              <div className="flex justify-between py-2 border-b dark:border-slate-800">
                <span className="text-sm text-slate-500">User Email</span>
                <span className="text-sm font-medium dark:text-white">{viewModal.order.user_email}</span>
              </div>
              <div className="flex justify-between py-2 border-b dark:border-slate-800">
                <span className="text-sm text-slate-500">Course</span>
                <span className="text-sm font-medium dark:text-white">{viewModal.order.course_title}</span>
              </div>
              <div className="flex justify-between py-2 border-b dark:border-slate-800">
                <span className="text-sm text-slate-500">Amount Paid</span>
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">₹{viewModal.order.amount}</span>
              </div>
              <div className="flex justify-between py-2 border-b dark:border-slate-800">
                <span className="text-sm text-slate-500">Payment Method</span>
                <span className="text-sm font-black uppercase text-slate-800 dark:text-white">{viewModal.order.payment_mode || "Online"}</span>
              </div>
              <div className="flex justify-between py-2 border-b dark:border-slate-800">
                <span className="text-sm text-slate-500">Date</span>
                <span className="text-sm dark:text-white">{new Date(viewModal.order.created_at).toLocaleString()}</span>
              </div>
            </div>
            <div className="mt-8">
              <button onClick={() => setViewModal({ open: false, order: null })} className="w-full py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white font-bold hover:bg-slate-200 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
  );
}
